import moment from 'moment'
import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { EuiBadge, EuiButtonEmpty, EuiEmptyPrompt, EuiFlexGroup, EuiFlexItem, EuiLink, EuiLoadingChart, EuiPage, EuiPageBody, EuiPageHeader, EuiPageHeaderSection, EuiPanel, EuiSpacer, EuiTitle, SortDirection } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { Campaign, CampaignActiveStatus, CampaignStatus } from 'api/interfaces'
import { useGetCampaignsQuery } from 'api/rtkQueryApi/platform/campaignsApi'
import { RootState } from 'app/rootReducer'
import { AcEuiInMemoryTableWithSearch } from 'components/Basic/AcEuiInMemoryTableWithSearch'
import { CampaignProgress } from 'components/Campaign/CampaignProgress'
import { CampaignStatusToElasticColor, CampaignStatusToString } from 'utils/EnumToFriendly'
import { formatCurrency } from 'utils/Formatters'
import { useWhiteLabel } from 'whiteLabel/WhiteLabelContext'

const CampaignReportsPage: React.FC = () => {
  const history = useHistory()
  const whiteLabel = useWhiteLabel()
  const { currentAccount } = useSelector((state: RootState) => state.app)

  const { data: campaigns, isFetching: isCampaignsFetching } = useGetCampaignsQuery({ accountId: currentAccount?.id ?? '' }, { skip: !currentAccount?.id })

  const [activeCampaigns, setActiveCampaigns] = useState<Campaign[]>([])
  const [pastCampaigns, setPastCampaigns] = useState<Campaign[]>([])

  useEffect(() => {
    if (campaigns) {
      const campaignRows: Campaign[] = campaigns.map(campaign => campaign)

      setActiveCampaigns(campaignRows.filter(campaign => campaign.activeStatus === CampaignActiveStatus.Active))
      setPastCampaigns(campaignRows.filter(campaign => campaign.activeStatus === CampaignActiveStatus.Inactive && !!campaign.activated && campaign.status !== CampaignStatus.SavedForLater && campaign.status !== CampaignStatus.Archived))
    }
  }, [campaigns])

  const handleViewClick = (campaignId: string) => {
    history.push(`/reports/${campaignId}`)
  }

  const startDateColumn: EuiBasicTableColumn<Campaign> = {
    width: '90px',
    name: `Start Date`,
    render: (campaign: Campaign) => {
      const startDate = campaign.startDate ?? campaign.activated

      if (!startDate) {
        return ''
      }

      return moment(startDate).format('MM/DD/yyyy')
    },
    sortable: (campaign: Campaign) => moment(campaign.startDate ?? campaign.activated).valueOf()
  }

  const campaignNameColumn: EuiBasicTableColumn<Campaign> = {
    width: '200px',
    field: 'name',
    name: 'Campaign Name',
    render: (name: string, row: Campaign) => (
      <EuiLink color='success' onClick={() => handleViewClick(row.id)}>
        {name}
      </EuiLink>
    ),
    sortable: (c: Campaign) => c.name
  }

  const typeColumn: EuiBasicTableColumn<Campaign> = {
    width: '90px',
    name: `Type`,
    render: (c: Campaign) => <div className='campaignType'>{c.type}</div>,
    sortable: (c: Campaign) => c.type
  }

  const budgetColumn: EuiBasicTableColumn<Campaign> = {
    width: '90px',
    name: `Budget`,
    render: (c: Campaign) => {
      return formatCurrency(c.budget)
    }
  }

  const timelineColumn: EuiBasicTableColumn<Campaign> = {
    width: '150px',
    name: `Timeline`,
    render: (c: Campaign) => {
      return <CampaignProgress campaign={c} />
    }
  }

  const deliveryColumn: EuiBasicTableColumn<Campaign> = {
    width: '150px',
    name: `Delivery`,
    render: (c: Campaign) => <EuiBadge color={CampaignStatusToElasticColor(c.status)}>{CampaignStatusToString(c)}</EuiBadge>
  }

  const activeCampaignsColumns: Array<EuiBasicTableColumn<Campaign>> = [startDateColumn, campaignNameColumn, typeColumn, budgetColumn, timelineColumn, deliveryColumn]
  const pastCampaignsColumns: Array<EuiBasicTableColumn<Campaign>> = [startDateColumn, campaignNameColumn, typeColumn, budgetColumn, timelineColumn, deliveryColumn]

  const activeCampaignsSorting = {
    allowNeutralSort: true,
    enableAllColumns: false,
    sort: {
      field: 'Start Date',
      direction: SortDirection.DESC
    }
  }

  const pastCampaignsSorting = {
    allowNeutralSort: true,
    enableAllColumns: false,
    sort: {
      field: 'End Date',
      direction: SortDirection.DESC
    }
  }

  const PageLayout: FC = ({ children }) => (
    <React.Fragment>
      <Helmet>
        <title>Campaign Reports</title>
      </Helmet>

      <EuiPage className='euiPage--platform'>
        <EuiPageBody>
          <EuiPageHeader>
            <EuiPageHeaderSection>
              <EuiTitle>
                <h1>Reports{whiteLabel?.isAgencies() && `: ${currentAccount?.name}`}</h1>
              </EuiTitle>
            </EuiPageHeaderSection>
            <EuiPageHeaderSection>
              <EuiButtonEmpty color='primary' onClick={() => history.push('/legacy-reports')}>
                View Legacy Reports
              </EuiButtonEmpty>
            </EuiPageHeaderSection>
          </EuiPageHeader>

          <EuiSpacer size='l' />

          {children}
        </EuiPageBody>
      </EuiPage>
    </React.Fragment>
  )

  if (isCampaignsFetching) {
    return (
      <PageLayout>
        <EuiFlexGroup justifyContent='spaceAround' alignItems='flexStart'>
          <EuiFlexItem grow={false}>
            <EuiLoadingChart size='xl' />
          </EuiFlexItem>
        </EuiFlexGroup>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <EuiPanel>
        <EuiTitle size='s'>
          <h2>Active Campaigns</h2>
        </EuiTitle>
        <EuiSpacer size='l' />

        {activeCampaigns.length <= 0 && <EuiEmptyPrompt title={<h3>No active campaigns</h3>} body="You don't have any active campaigns at the moment." titleSize='xs' />}
        {activeCampaigns.length > 0 && (
          <AcEuiInMemoryTableWithSearch
            items={activeCampaigns}
            columns={activeCampaignsColumns}
            initialPageSize={10}
            pageSizeOptions={[10, 25, 50, 100]}
            sorting={activeCampaignsSorting}
            search={{
              box: {
                incremental: true
              }
            }}
            searchFields={['name']}
          />
        )}
      </EuiPanel>

      <EuiSpacer size='xxl' />

      <EuiPanel>
        <EuiTitle size='s'>
          <h2>Past Campaigns</h2>
        </EuiTitle>
        <EuiSpacer size='l' />

        {pastCampaigns.length <= 0 && <EuiEmptyPrompt title={<h3>No past campaigns</h3>} body="You don't have any past campaigns to display." titleSize='xs' />}
        {pastCampaigns.length > 0 && (
          <AcEuiInMemoryTableWithSearch
            items={pastCampaigns}
            columns={pastCampaignsColumns}
            initialPageSize={10}
            pageSizeOptions={[10, 25, 50, 100]}
            sorting={pastCampaignsSorting}
            search={{
              box: {
                incremental: true
              }
            }}
            searchFields={['name']}
          />
        )}
      </EuiPanel>
    </PageLayout>
  )
}

export default CampaignReportsPage
